/**
 * AdditionalVehicleBlock
 *
 * Renders the same vehicle fields as Step3Vehicle but parameterized by an
 * index into step3.additionalVehicles. Rendered inside Step3Vehicle's
 * useFieldArray mapping.
 */

import { useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Trash2 } from "lucide-react";
import { FormField } from "@/components/ui/FormField";
import { AnimatedReveal } from "@/components/ui/AnimatedReveal";
import {
  VEHICLE_USE_OPTIONS,
  VEHICLE_USE_LABELS,
  type FullFormData,
} from "@/schemas/formSchemas";

interface AdditionalVehicleBlockProps {
  index: number;
  onRemove: () => void;
}

export function AdditionalVehicleBlock({
  index,
  onRemove,
}: AdditionalVehicleBlockProps) {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<FullFormData>();

  const basePath = `step3.additionalVehicles.${index}` as const;
  const purchaseMethod = watch(`${basePath}.vehicleOriginalPurchaseMethod`);
  const payoffStatus = watch(`${basePath}.vehicleCurrentPayoffStatus`);
  const primaryUse = watch(`${basePath}.primaryVehicleUse`);
  const boughtCondition = watch(`${basePath}.vehicleBoughtCondition`);

  const paidInFull = purchaseMethod === "paid_in_full";
  const showLienHolder =
    !paidInFull &&
    (payoffStatus === "still_financed" || payoffStatus === "still_leased");

  useEffect(() => {
    if (paidInFull) {
      if (payoffStatus !== "fully_paid_off") {
        setValue(`${basePath}.vehicleCurrentPayoffStatus`, "fully_paid_off", {
          shouldValidate: true,
        });
      }
      setValue(`${basePath}.vehicleLienHolder`, "");
    } else if (payoffStatus === "fully_paid_off") {
      setValue(
        `${basePath}.vehicleCurrentPayoffStatus`,
        "" as FullFormData["step3"]["vehicleCurrentPayoffStatus"],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidInFull]);

  const vehicleErrors = errors.step3?.additionalVehicles?.[index];

  return (
    <div
      className="relative mb-6"
      style={{
        border: "1px solid var(--border-default, #E5E7EB)",
        borderRadius: 16,
        padding: "24px 20px",
        background: "var(--bg-muted, #FAFAFA)",
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className="text-base font-semibold"
          style={{ color: "var(--color-navy)" }}
        >
          Vehicle {index + 2}
        </h3>
        <button
          type="button"
          onClick={onRemove}
          className="flex items-center gap-1 text-sm"
          style={{
            color: "var(--text-secondary)",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "4px 8px",
          }}
        >
          <Trash2 size={14} strokeWidth={2} />
          Remove
        </button>
      </div>

      <FormField
        label="Year"
        name={`${basePath}.vehicleYear`}
        required
        error={vehicleErrors?.vehicleYear?.message}
      >
        <Controller
          name={`${basePath}.vehicleYear`}
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              placeholder={new Date().getFullYear().toString()}
              className={`form-input ${
                vehicleErrors?.vehicleYear ? "form-input-error" : ""
              }`}
              onChange={(e) =>
                field.onChange(e.target.value ? parseInt(e.target.value) : "")
              }
            />
          )}
        />
      </FormField>

      <FormField
        label="Make"
        name={`${basePath}.vehicleMake`}
        required
        error={vehicleErrors?.vehicleMake?.message}
      >
        <Controller
          name={`${basePath}.vehicleMake`}
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoCapitalize="words"
              placeholder="Toyota"
              className={`form-input ${
                vehicleErrors?.vehicleMake ? "form-input-error" : ""
              }`}
            />
          )}
        />
      </FormField>

      <FormField
        label="Model"
        name={`${basePath}.vehicleModel`}
        required
        error={vehicleErrors?.vehicleModel?.message}
      >
        <Controller
          name={`${basePath}.vehicleModel`}
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoCapitalize="words"
              placeholder="Camry"
              className={`form-input ${
                vehicleErrors?.vehicleModel ? "form-input-error" : ""
              }`}
            />
          )}
        />
      </FormField>

      <FormField
        label="Purchase Date"
        name={`${basePath}.vehiclePurchaseDate`}
        required
        error={vehicleErrors?.vehiclePurchaseDate?.message}
        helperText="Format: MM/YYYY"
      >
        <Controller
          name={`${basePath}.vehiclePurchaseDate`}
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              inputMode="numeric"
              pattern="[0-9/]*"
              maxLength={7}
              placeholder="MM/YYYY"
              className={`form-input ${
                vehicleErrors?.vehiclePurchaseDate ? "form-input-error" : ""
              }`}
            />
          )}
        />
      </FormField>

      <FormField
        label="Bought As"
        name={`${basePath}.vehicleBoughtCondition`}
        required
        error={vehicleErrors?.vehicleBoughtCondition?.message}
      >
        <Controller
          name={`${basePath}.vehicleBoughtCondition`}
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className={`form-input ${
                vehicleErrors?.vehicleBoughtCondition ? "form-input-error" : ""
              }`}
            >
              <option value="">Select condition</option>
              <option value="certified_pre_owned">Certified Pre-Owned</option>
              <option value="new">New</option>
              <option value="used">Used</option>
            </select>
          )}
        />
      </FormField>

      <AnimatedReveal isVisible={boughtCondition === "certified_pre_owned"}>
        <p
          className="-mt-4 mb-6 text-xs"
          style={{ color: "var(--text-secondary)" }}
        >
          Certified Pre-Owned means a used vehicle inspected and backed by the
          manufacturer's warranty.
        </p>
      </AnimatedReveal>

      <FormField
        label="How Did You Purchase It?"
        name={`${basePath}.vehicleOriginalPurchaseMethod`}
        required
        error={vehicleErrors?.vehicleOriginalPurchaseMethod?.message}
      >
        <Controller
          name={`${basePath}.vehicleOriginalPurchaseMethod`}
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className={`form-input ${
                vehicleErrors?.vehicleOriginalPurchaseMethod
                  ? "form-input-error"
                  : ""
              }`}
            >
              <option value="">Select method</option>
              <option value="financed">Financed</option>
              <option value="leased">Leased</option>
              <option value="paid_in_full">Paid in Full</option>
            </select>
          )}
        />
      </FormField>

      <AnimatedReveal isVisible={!paidInFull}>
        <FormField
          label="Current Payoff Status"
          name={`${basePath}.vehicleCurrentPayoffStatus`}
          required
          error={vehicleErrors?.vehicleCurrentPayoffStatus?.message}
        >
          <Controller
            name={`${basePath}.vehicleCurrentPayoffStatus`}
            control={control}
            render={({ field }) => (
              <select
                {...field}
                className={`form-input ${
                  vehicleErrors?.vehicleCurrentPayoffStatus
                    ? "form-input-error"
                    : ""
                }`}
              >
                <option value="">Select status</option>
                <option value="fully_paid_off">Fully Paid Off</option>
                <option value="still_financed">Still Financed</option>
                <option value="still_leased">Still Leased</option>
              </select>
            )}
          />
        </FormField>
      </AnimatedReveal>

      <AnimatedReveal isVisible={showLienHolder}>
        <FormField
          label="Lien Holder / Lessor Name"
          name={`${basePath}.vehicleLienHolder`}
          required
          error={vehicleErrors?.vehicleLienHolder?.message}
        >
          <Controller
            name={`${basePath}.vehicleLienHolder`}
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                autoCapitalize="words"
                placeholder="Chase Bank, BMW Financial Services, etc."
                className={`form-input ${
                  vehicleErrors?.vehicleLienHolder ? "form-input-error" : ""
                }`}
              />
            )}
          />
        </FormField>
      </AnimatedReveal>

      <FormField
        label="Annual Mileage"
        name={`${basePath}.annualMileage`}
        required
        error={vehicleErrors?.annualMileage?.message}
      >
        <Controller
          name={`${basePath}.annualMileage`}
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="12000"
              className={`form-input ${
                vehicleErrors?.annualMileage ? "form-input-error" : ""
              }`}
              onChange={(e) =>
                field.onChange(e.target.value ? parseInt(e.target.value) : "")
              }
            />
          )}
        />
      </FormField>

      <FormField
        label="Primary use of this vehicle"
        name={`${basePath}.primaryVehicleUse`}
        required
        error={vehicleErrors?.primaryVehicleUse?.message}
      >
        <Controller
          name={`${basePath}.primaryVehicleUse`}
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className={`form-input ${
                vehicleErrors?.primaryVehicleUse ? "form-input-error" : ""
              }`}
            >
              <option value="">Select use</option>
              {VEHICLE_USE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {VEHICLE_USE_LABELS[option]}
                </option>
              ))}
            </select>
          )}
        />
      </FormField>

      <AnimatedReveal isVisible={primaryUse === "commute"}>
        <FormField
          label="One-way commute distance (miles)"
          name={`${basePath}.commuteMileage`}
          required
          error={vehicleErrors?.commuteMileage?.message}
        >
          <Controller
            name={`${basePath}.commuteMileage`}
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="15"
                className={`form-input ${
                  vehicleErrors?.commuteMileage ? "form-input-error" : ""
                }`}
                onChange={(e) =>
                  field.onChange(e.target.value ? parseInt(e.target.value) : "")
                }
              />
            )}
          />
        </FormField>
      </AnimatedReveal>
    </div>
  );
}
